import { ref, Ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import * as TE from 'fp-ts/lib/TaskEither';
import * as T from 'fp-ts/lib/Task';
import { pipe } from 'fp-ts/lib/pipeable';
import { ApiError } from '@/services/api';
import { isDev } from '@/ui/helpers/systemHelper';

export interface MutatorFunction<T, I> {
  (data: T, input?: I): void;
}

// This is the state of the async operation, represented with a
// discriminated union. This way it is impossible to set an
// invalid state.
export type TaskState =
  { pending: false }
  | { pending: true }
  | { pending: false; success: true }
  | { pending: false; error: ApiError };

// Global store for all async operations identified by a string key.
// Each run gets a random value and sets that value for the key.
// When the operation is done, before executing the mutation,
// we check if the current run is the last run, in which case
// the value will still be the same. If it is not the same, some other
// run of the same process started in the meantime, and the results
// for this run are ignored.
const asyncProcessIDs: Record<string, string> = {};

export interface Task<I = undefined, T = unknown> {
  run: (input?: I) => Promise<T>;
  state: Ref<TaskState>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const dummyMutator = () => {};
// eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
const dummyErrorFunction = (error: ApiError) => {};

export function useTask<T, I>(
  fn: (input?: I) => TE.TaskEither<ApiError, T>,
  mutatorFunction: MutatorFunction<T, I> = dummyMutator,
  key = uuidv4(),
  onErrorFunction = dummyErrorFunction,
): Task<I, T> {
  // state
  const state = ref<TaskState>({ pending: false });

  // executable process
  // TODO: find a way to make input optional AND keep proper typings. maybe use Option<I>?
  // @ts-ignore
  const run = (input?: I = null): Promise<T> => {
    state.value = { pending: true };

    // generate random kay and store it for this async operation identified by 'key'
    const id = uuidv4();
    asyncProcessIDs[key] = id;

    return new Promise<T>(resolve => {
      pipe(
        fn(input),
        TE.fold(
          error => T.task.fromIO(() => {
            onErrorFunction(error);

            if (asyncProcessIDs[key] === id) {
              isDev && console.error(error);
              state.value = { pending: false, error };
              delete asyncProcessIDs[key];
            } else {
              // console.info('not last fetch, ignoring error', key, asyncProcessIDs[key], id);
            }
          }),
          result => T.task.fromIO(() => {
            if (asyncProcessIDs[key] === id) {
              input === null ? mutatorFunction(result) : mutatorFunction(result, input);
              state.value = { pending: false, success: true };
              delete asyncProcessIDs[key];
              resolve(result);
            } else {
              // console.info('not last fetch, ignoring mutation', key, asyncProcessIDs[key], id);
            }
          }),
        ),
      )();
    });
  };

  return { run, state };
}

export const dummyTask = useTask(() => TE.right([]));

export function syncRefWithTaskState(loadingRef: Ref<boolean>, task: Task) {
  const unwatch = watch(
    () => task.state.value.pending,
    (pending, oldValue) => {
      loadingRef.value = pending;

      if (oldValue && !pending) {
        unwatch();
      }
    },
    { immediate: true },
  );
}
